import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";

const ROLE_LABEL = { super_admin: "Super Admin", hr_admin: "HR Admin", manager: "Manager", employee: "Employee" };
const DEPARTMENTS = ["Admin", "CSR", "Sales", "Quoting Team", "Service Team"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const EMPTY_FORM = { email: "", full_name: "", role: "employee", job_title: "", department: "CSR", hire_date: "", pay_type: "hourly", hourly_rate: "", annual_salary: "", pay_schedule: "biweekly", licensed_states: [], reviewer_id: "" };
const ROLE_STYLE = {
  super_admin: "bg-indigo-100 text-indigo-800",
  hr_admin: "bg-violet-100 text-violet-800",
  manager: "bg-sky-100 text-sky-800",
  employee: "bg-slate-100 text-slate-700",
};

export default function TeamPage() {
  const { hasPerm } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const navigate = useNavigate();

  const load = () => api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createMember = async () => {
    try {
      await api.post("/users", {
        ...form,
        reviewer_id: form.reviewer_id || null,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        annual_salary: parseFloat(form.annual_salary) || 0,
      });
      toast.success(form.licensed_states.length
        ? "Team member added with pay setup — they'll be emailed to upload their state licenses"
        : "Team member added with pay setup");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add member");
    }
  };

  const toggleState = (s) => setForm((f) => ({
    ...f, licensed_states: f.licensed_states.includes(s) ? f.licensed_states.filter((x) => x !== s) : [...f.licensed_states, s],
  }));

  const reviewers = members.filter((u) => u.is_active !== false && (u.role === "super_admin" || (u.permission_overrides || {})["reviews.complete"] === true));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">People</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Team directory</h1>
        </div>
        {hasPerm("users.manage") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-member-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Plus size={16} className="mr-1.5" /> Add member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="add-member-dialog" aria-describedby={undefined}>
              <DialogHeader><DialogTitle className="font-heading">Add team member</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Full name</Label><Input data-testid="new-member-name" className="mt-1.5" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                <div><Label>Work email</Label><Input data-testid="new-member-email" type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Job title</Label><Input data-testid="new-member-title" className="mt-1.5" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
                <div>
                  <Label>Start date</Label>
                  <Input data-testid="new-member-hire-date" type="date" className="mt-1.5" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
                  <p className="text-xs text-muted-foreground mt-1">Used for work-anniversary milestone reminders (1, 3, 5, 10… years).</p>
                </div>
                <div>
                  <Label>Department</Label>
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                    <SelectTrigger data-testid="new-member-department" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-member-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(ROLE_LABEL).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.role !== "super_admin" && (
                  <div>
                    <Label>Performance reviewer <span className="text-red-500">*</span></Label>
                    <Select value={form.reviewer_id} onValueChange={(v) => setForm({ ...form, reviewer_id: v })}>
                      <SelectTrigger data-testid="new-member-reviewer" className="mt-1.5"><SelectValue placeholder="Pick who runs their annual reviews" /></SelectTrigger>
                      <SelectContent>
                        {reviewers.map((r) => <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">Required — runs this member's annual performance reviews. Only people with the "Complete reviews" ability (or super admins) qualify.</p>
                  </div>
                )}
                <div className="border-t border-border pt-4 space-y-4">
                  <p className="text-sm font-semibold font-heading tracking-tight">Pay setup</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Pay type</Label>
                      <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
                        <SelectTrigger data-testid="new-member-pay-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="salary">Salary</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Pay schedule</Label>
                      <Select value={form.pay_schedule} onValueChange={(v) => setForm({ ...form, pay_schedule: v })}>
                        <SelectTrigger data-testid="new-member-pay-schedule" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="biweekly">Bi-weekly</SelectItem><SelectItem value="semimonthly">Semi-monthly (1st & 15th)</SelectItem><SelectItem value="monthly">Monthly (15th)</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  {form.pay_type === "hourly" ? (
                    <div><Label>Hourly rate ($) <span className="text-red-500">*</span></Label><Input data-testid="new-member-hourly-rate" type="number" step="0.01" className="mt-1.5" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} /></div>
                  ) : (
                    <div><Label>Annual salary ($) <span className="text-red-500">*</span></Label><Input data-testid="new-member-annual-salary" type="number" step="100" className="mt-1.5" value={form.annual_salary} onChange={(e) => setForm({ ...form, annual_salary: e.target.value })} /></div>
                  )}
                </div>
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-semibold font-heading tracking-tight mb-1">Licensed states</p>
                  <p className="text-xs text-muted-foreground mb-2">States this member can write insurance in — they'll be emailed to upload a license for each.</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="new-member-states">
                    {US_STATES.map((s) => (
                      <button key={s} type="button" data-testid={`new-member-state-${s}`} onClick={() => toggleState(s)}
                        className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${
                          form.licensed_states.includes(s) ? "bg-indigo-600 text-white border-indigo-600" : "bg-card text-muted-foreground border-border hover:border-indigo-400"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <Button data-testid="create-member-btn" onClick={createMember}
                  disabled={!form.email || !form.full_name || (form.role !== "super_admin" && !form.reviewer_id) || (form.pay_type === "hourly" ? !(parseFloat(form.hourly_rate) > 0) : !(parseFloat(form.annual_salary) > 0))}
                  className="w-full bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">Create account</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 px-6 py-3 border-b border-border text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">
          <span className="col-span-5">Member</span><span className="col-span-3">Role</span><span className="col-span-2">PTO</span><span className="col-span-2">Sick</span>
        </div>
        <div className="divide-y divide-border">
          {members.map((m, i) => (
            <button key={m.id} data-testid={`member-row-${m.email.split("@")[0]}`}
              onClick={() => navigate(`/team/${m.id}`)}
              className="w-full grid grid-cols-12 items-center px-4 sm:px-6 py-3.5 text-left hover:bg-muted transition-colors rise-in"
              style={{ animationDelay: `${i * 40}ms` }}>
              <div className="col-span-8 sm:col-span-5 flex items-center gap-3 min-w-0">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={m.photo_file_path ? fileUrl(m.photo_file_path) : m.avatar_url} />
                  <AvatarFallback className="bg-slate-200 text-xs">{m.full_name?.split(" ").map((w) => w[0]).join("").slice(0, 2)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{m.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.job_title || m.email}</p>
                </div>
              </div>
              <div className="col-span-4 sm:col-span-3"><Badge className={ROLE_STYLE[m.role]}>{ROLE_LABEL[m.role]}</Badge></div>
              <span className="hidden sm:block col-span-2 text-sm">{m.department || "—"}</span>
              <span className="hidden sm:block col-span-2 text-sm">{m.sick_balance_hours}h</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
